import React, { useMemo } from "react"
import { Row, Col, Typography, Divider } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { colors } from "themes/variables"

const { Text } = Typography

const PreviousPriceDetail = ({ booking }) => {
  const { payment_summary = {}, payment_history = [] } = booking || {}
  const {
    total_price,
    tax_and_fee,
    adult,
    child,
    infant,
    remaining_balance,
    passenger,
    pricing_type,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    flat_rate
  } = payment_summary ?? {}
  const { t } = useTranslation()
  const summaryDetails = useMemo(() => {
    const details = []
    
    const addPricingDetails = (type, count, price, label) => {
      if (count && price) {
        details.push({ label: `${label} x ${count}`, value: price })
      }
    }

    const addPerPersonPricing = () => {
      if (pricing_type !== "charge_per_person") return
      
      addPricingDetails(adult, adult?.count, adult?.price, 'Adult')
      addPricingDetails(child, child?.count, child?.price, 'Child')
      addPricingDetails(infant, infant?.count, infant?.price, 'Infant')
      addPricingDetails(passenger, passenger?.count, passenger?.price, 'Passenger')
    }

    const addFlatRatePricing = () => {
      if (pricing_type !== "charge_flat_rate") return
      
      details.push({ label: "Flat Rate For Main Guests", value: flat_rate ?? 0 })
      addPricingDetails(extra_adult, extra_adult?.count, extra_adult?.price, 'Extra Adult')
      addPricingDetails(extra_child, extra_child?.count, extra_child?.price, 'Extra Child')
      addPricingDetails(extra_infant, extra_infant?.count, extra_infant?.price, 'Extra Infant')
      addPricingDetails(extra_passenger, extra_passenger?.count, extra_passenger?.price, 'Extra Passenger')
    }
    
    addPerPersonPricing()
    addFlatRatePricing()
    
    details.push({ label: "Tax & Fees", value: tax_and_fee ?? 0 })
    return details
  }, [
    adult, child, infant, passenger, 
    tax_and_fee, flat_rate, extra_adult, 
    extra_child, extra_infant, extra_passenger, 
    pricing_type
  ])

  return (
    <div>
      {summaryDetails
        .filter(({ value }) => value !== undefined && value !== null && value !== "")
        .map(({ label, value, fw }) => (
          <Row key={`${label}-${value}`} justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text fw={fw || 400}>{label}</Text>
            </Col>
            <Col>
              <Text fw={500}>&nbsp;&nbsp;{numberToCurrency(value)}</Text>
            </Col>
          </Row>
        ))}
      <Divider style={{ margin: "16px 0" }} />

      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.total")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(total_price)}</Text>
        </Col>
      </Row>
      {payment_history?.map((history) => {
        const { type, method, last4, amount, id } = history || {}
        const isRefund = type?.toLowerCase() === "refund"
        const key = id || `${type}-${method}-${last4}-${amount}`
        return (
          <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text>
                {type || ""} ({method || ""}
                {last4 && ` **** ${last4}`})
              </Text>
            </Col>
            <Col>
              <Text color={isRefund ? colors.error : colors.textPrimary}>{amount ? numberToCurrency(amount) : ""}</Text>
            </Col>
          </Row>
        )
      })}
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.remaining_balance")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(remaining_balance)}</Text>
        </Col>
      </Row>
    </div>
  )
}

PreviousPriceDetail.propTypes = {
  booking: PropTypes.object.isRequired,
}

export default PreviousPriceDetail
